import { useState, useMemo } from "react";
import { Search, Sprout } from "lucide-react";
import { useDroppable } from "@dnd-kit/core";
import { Input } from "@/components/ui/input";
import { CropChip } from "./CropChip";

/**
 * Sidebar listing crops not yet placed on the bed grid.
 * Doubles as a drop zone — dropping a placed crop here un-places it.
 */
export function CropPalette({ unplaced = [], onEditCrop, onDeleteCrop }) {
    const [q, setQ] = useState("");
    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return unplaced;
        return unplaced.filter(c =>
            (c.crop_name || "").toLowerCase().includes(needle) ||
            (c.variety || "").toLowerCase().includes(needle)
        );
    }, [q, unplaced]);

    const { setNodeRef, isOver, active } = useDroppable({
        id: "palette-dropzone",
        data: { kind: "palette" },
    });

    // Only highlight when a placed crop is being dragged (un-place gesture)
    const sourceFromGrid = active?.data?.current?.fromPosition;
    const showHover = isOver && !!sourceFromGrid;

    return (
        <aside
            ref={setNodeRef}
            data-testid="crop-palette"
            className={`rounded-lg border bg-[var(--vm-card)] p-4 w-full md:w-72 shrink-0 transition-colors
                ${showHover
                    ? "border-[var(--vm-sage)] ring-2 ring-[var(--vm-sage)] ring-offset-1 ring-offset-[var(--vm-bg)]"
                    : "border-[var(--vm-border)]"}`}
        >
            <div className="overline text-[var(--vm-sage)] mb-2">
                Unplaced crops
                {showHover && <span className="ml-2 text-[var(--vm-accent)]">— drop to un-place</span>}
            </div>
            <div className="relative mb-3">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--vm-text-muted)]" />
                <Input
                    data-testid="palette-search"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="Search crops…"
                    className="pl-8 bg-[var(--vm-input)] border-[var(--vm-border)] h-9 text-sm"
                />
            </div>

            {filtered.length === 0 ? (
                <div className="text-center py-8 text-[var(--vm-text-muted)]" data-testid="palette-empty">
                    <Sprout className="w-6 h-6 mx-auto mb-2 text-[var(--vm-sage)]" strokeWidth={1.5} />
                    <p className="text-xs">
                        {q
                            ? "No crops match your search"
                            : sourceFromGrid
                                ? "Drop here to un-place"
                                : "Every crop has a home"}
                    </p>
                </div>
            ) : (
                <ul className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="palette-list">
                    {filtered.map(c => (
                        <li key={c.id}>
                            <CropChip crop={c} variant="palette" draggable onEdit={onEditCrop} onDelete={onDeleteCrop} />
                        </li>
                    ))}
                </ul>
            )}

            <p className="text-[10px] text-[var(--vm-text-muted)] mt-3 leading-relaxed">
                Drag onto the grid to place. Drop a placed crop here to free its cell.
            </p>
        </aside>
    );
}
